import { Node, Mark, Extension, mergeAttributes } from "@tiptap/core";
import { Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";
import { ReplaceStep } from "@tiptap/pm/transform";
import { Fragment, Slice } from "@tiptap/pm/model";
import Image from "@tiptap/extension-image";

export const Section = Node.create({
  name: "section", group: "section", content: "block+", defining: true, isolating: true,
  addAttributes() { return { id: { default: null }, sectionType: { default: "FACTS" }, title: { default: "Section" }, required: { default: false } }; },
  parseHTML() { return [{ tag: "section[data-section-id]", getAttrs: (el) => ({ id: el.getAttribute("data-section-id"), sectionType: el.getAttribute("data-section-type"), title: el.getAttribute("data-title"), required: el.getAttribute("data-required") === "true" }) }]; },
  renderHTML({ node, HTMLAttributes }) {
    return ["section", mergeAttributes(HTMLAttributes, { "data-section-id": node.attrs.id, "data-section-type": node.attrs.sectionType, "data-title": node.attrs.title, "data-required": String(node.attrs.required), class: "nyk-section", id: node.attrs.id }),
      ["div", { class: "nyk-section-title", contenteditable: "false" }, node.attrs.title], ["div", { class: "nyk-section-body" }, 0]];
  },
});

export const PageBreak = Node.create({
  name: "pageBreak", group: "block", atom: true, selectable: true,
  parseHTML() { return [{ tag: "div[data-page-break]" }]; },
  renderHTML() { return ["div", { "data-page-break": "true", class: "nyk-page-break", contenteditable: "false" }, ["span", {}, "Page break"]]; },
  addCommands() { return { insertPageBreak: () => ({ chain }) => chain().insertContent({ type: this.name }).insertContent({ type: "paragraph" }).run() }; },
  addKeyboardShortcuts() { return { "Mod-Enter": () => this.editor.commands.insertPageBreak() }; },
});

export const Footnote = Node.create({
  name: "footnote", group: "inline", inline: true, atom: true,
  addAttributes() { return { text: { default: "" } }; },
  parseHTML() { return [{ tag: "sup[data-footnote]", getAttrs: (el) => ({ text: el.getAttribute("data-footnote") }) }]; },
  renderHTML({ node }) { return ["sup", { "data-footnote": node.attrs.text, class: "nyk-footnote", title: node.attrs.text }]; },
});

export const Citation = Node.create({
  name: "citation", group: "inline", inline: true, atom: true,
  addAttributes() { return { label: { default: "" }, authorityId: { default: null }, propositionId: { default: null }, para: { default: null }, verification: { default: null }, linkId: { default: null } }; },
  parseHTML() { return [{ tag: "span[data-citation]", getAttrs: (el) => ({ label: el.textContent, authorityId: el.getAttribute("data-authority-id"), para: el.getAttribute("data-para"), verification: el.getAttribute("data-verification"), linkId: el.getAttribute("data-link-id") }) }]; },
  renderHTML({ node }) { return ["span", { "data-citation": "true", "data-authority-id": node.attrs.authorityId, "data-para": node.attrs.para, "data-verification": node.attrs.verification, "data-link-id": node.attrs.linkId, class: "nyk-citation" }, node.attrs.label]; },
});

export const SignatureBlock = Node.create({
  name: "signatureBlock", group: "block", atom: true,
  addAttributes() { return { lines: { default: [] } }; },
  parseHTML() { return [{ tag: "div[data-signature]" }]; },
  renderHTML({ node }) { return ["div", { "data-signature": "true", class: "nyk-signature" }, ...node.attrs.lines.map((l) => ["div", {}, l])]; },
});

export const Bookmark = Mark.create({
  name: "bookmark",
  addAttributes() { return { name: { default: "" } }; },
  parseHTML() { return [{ tag: "a[data-bookmark]", getAttrs: (el) => ({ name: el.getAttribute("data-bookmark") }) }]; },
  renderHTML({ mark }) { return ["a", { "data-bookmark": mark.attrs.name, id: `bm-${mark.attrs.name}`, class: "nyk-bookmark" }, 0]; },
});

export const CommentMark = Mark.create({
  name: "comment", inclusive: false,
  addAttributes() { return { id: { default: null }, resolved: { default: false } }; },
  parseHTML() { return [{ tag: "span[data-comment-id]", getAttrs: (el) => ({ id: el.getAttribute("data-comment-id") }) }]; },
  renderHTML({ mark }) { return ["span", { "data-comment-id": mark.attrs.id, class: `nyk-comment${mark.attrs.resolved ? " resolved" : ""}` }, 0]; },
});

const changeAttrs = () => ({ author: { default: null }, authorName: { default: null }, at: { default: null } });
export const Insertion = Mark.create({
  name: "insertion", inclusive: true, addAttributes: changeAttrs,
  parseHTML() { return [{ tag: "ins[data-author]", getAttrs: (el) => ({ author: el.getAttribute("data-author"), authorName: el.getAttribute("data-author-name"), at: el.getAttribute("data-at") }) }]; },
  renderHTML({ mark }) { return ["ins", { "data-author": mark.attrs.author, "data-author-name": mark.attrs.authorName, "data-at": mark.attrs.at, class: "nyk-ins", title: `Inserted by ${mark.attrs.authorName || "?"}` }, 0]; },
});
export const Deletion = Mark.create({
  name: "deletion", inclusive: false, addAttributes: changeAttrs,
  parseHTML() { return [{ tag: "del[data-author]", getAttrs: (el) => ({ author: el.getAttribute("data-author"), authorName: el.getAttribute("data-author-name"), at: el.getAttribute("data-at") }) }]; },
  renderHTML({ mark }) { return ["del", { "data-author": mark.attrs.author, "data-author-name": mark.attrs.authorName, "data-at": mark.attrs.at, class: "nyk-del", title: `Deleted by ${mark.attrs.authorName || "?"}` }, 0]; },
});

export const trackKey = new PluginKey("trackChanges");
// Track changes: rewrites plain edits into insertion/deletion marks while enabled.
export const TrackChanges = Extension.create({
  name: "trackChanges",
  addOptions() { return { enabled: false, user: { id: null, name: null } }; },
  addStorage() { return { enabled: false, user: { id: null, name: null } }; },
  addCommands() {
    return {
      setTrackChanges: (on) => ({ editor }) => { editor.storage.trackChanges.enabled = on; return true; },
      setTrackUser: (user) => ({ editor }) => { editor.storage.trackChanges.user = user; return true; },
      resolveChange: (from, to, type, accept) => ({ tr, state, dispatch }) => {
        tr.setMeta(trackKey, true);
        const mk = state.schema.marks[type];
        if ((type === "insertion" && accept) || (type === "deletion" && !accept)) tr.removeMark(from, to, mk);
        else tr.delete(from, to);
        if (dispatch) dispatch(tr);
        return true;
      },
      resolveAllChanges: (accept) => ({ tr, state, dispatch }) => {
        tr.setMeta(trackKey, true);
        const ranges = collectChanges(state.doc);
        ranges.sort((a, b) => b.from - a.from).forEach((r) => {
          const mk = state.schema.marks[r.type];
          if ((r.type === "insertion" && accept) || (r.type === "deletion" && !accept)) tr.removeMark(r.from, r.to, mk); else tr.delete(r.from, r.to);
        });
        if (dispatch) dispatch(tr);
        return true;
      },
    };
  },
  addProseMirrorPlugins() {
    const storage = this.storage;
    return [new Plugin({
      key: trackKey,
      appendTransaction(trs, oldState, newState) {
        if (!storage.enabled) return null;
        const changed = trs.filter((t) => t.docChanged && !t.getMeta(trackKey) && !t.getMeta("preventTrack"));
        if (!changed.length) return null;
        const schema = newState.schema;
        const ins = schema.marks.insertion, del = schema.marks.deletion;
        const user = storage.user || {};
        const attrs = { author: user.id, authorName: user.name, at: new Date().toISOString() };
        const tr = newState.tr.setMeta(trackKey, true);
        let selPos = null;
        changed.forEach((t, ti) => {
          t.steps.forEach((step, si) => {
            if (!(step instanceof ReplaceStep)) return;
            const before = t.docs[si];
            const laterMaps = [t.mapping.slice(si + 1), ...changed.slice(ti + 1).map((x) => x.mapping)];
            const map = (p) => laterMaps.reduce((acc, m) => m.map(acc), p);
            const from = map(step.from);
            if (step.slice.size) tr.addMark(from, from + step.slice.size, ins.create(attrs));
            if (step.to > step.from) {
              const deleted = before.slice(step.from, step.to);
              if (deleted.openStart || deleted.openEnd) return;
              const nodes = [];
              deleted.content.forEach((n) => {
                if (!n.isInline) return;
                if (n.marks.some((m) => m.type === ins && m.attrs.author === user.id)) return;
                if (n.marks.some((m) => m.type === del)) { nodes.push(n); return; }
                nodes.push(n.mark(n.marks.filter((m) => m.type !== ins).concat(del.create(attrs))));
              });
              if (!nodes.length) return;
              const insertAt = tr.mapping.map(from + step.slice.size);
              tr.insert(insertAt, Fragment.from(nodes));
              if (!step.slice.size) selPos = tr.mapping.map(from);
              else selPos = insertAt;
            }
          });
        });
        if (!tr.steps.length) return null;
        if (selPos !== null) tr.setSelection(TextSelection.create(tr.doc, Math.min(selPos, tr.doc.content.size)));
        return tr;
      },
    })];
  },
});

export function collectChanges(doc) {
  const out = [];
  doc.descendants((node, pos) => {
    if (!node.isText) return;
    node.marks.forEach((m) => { if (m.type.name === "insertion" || m.type.name === "deletion") out.push({ from: pos, to: pos + node.nodeSize, type: m.type.name, text: node.text, ...m.attrs }); });
  });
  const merged = [];
  out.forEach((r) => { const last = merged[merged.length - 1]; if (last && last.to === r.from && last.type === r.type && last.author === r.author) { last.to = r.to; last.text += r.text; } else merged.push({ ...r }); });
  return merged;
}

export const ParagraphAttrs = Extension.create({
  name: "paragraphAttrs",
  addGlobalAttributes() {
    const attr = (name, css, unit = "") => ({ default: null, parseHTML: (el) => el.style[css] ? el.style[css].replace(unit, "") : null, renderHTML: (a) => a[name] == null ? {} : { style: `${css.replace(/[A-Z]/g, (m) => "-" + m.toLowerCase())}: ${a[name]}${unit}` } });
    return [{ types: ["paragraph", "heading"], attributes: {
      indent: attr("indent", "marginLeft", "em"),
      firstLineIndent: attr("firstLineIndent", "textIndent", "mm"),
      lineHeight: attr("lineHeight", "lineHeight"),
      spaceBefore: attr("spaceBefore", "marginTop", "pt"),
      spaceAfter: attr("spaceAfter", "marginBottom", "pt"),
      keepWithNext: { default: false, renderHTML: (a) => a.keepWithNext ? { "data-keep-next": "true" } : {} },
      styleName: { default: null, renderHTML: (a) => a.styleName ? { "data-style": a.styleName, class: `nyk-style-${a.styleName}` } : {} },
    } }, { types: ["orderedList"], attributes: { listStyle: { default: "decimal", parseHTML: (el) => el.style.listStyleType || "decimal", renderHTML: (a) => ({ style: `list-style-type: ${a.listStyle}`, "data-list-style": a.listStyle }) } } }];
  },
  addCommands() {
    return {
      setIndent: (delta) => ({ tr, state, dispatch }) => {
        const { from, to } = state.selection;
        state.doc.nodesBetween(from, to, (node, pos) => { if (node.type.name === "paragraph" || node.type.name === "heading") { const cur = Number(node.attrs.indent || 0); tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: Math.max(0, cur + delta) || null }); } });
        if (dispatch) dispatch(tr); return true;
      },
      setParagraphAttr: (name, value) => ({ tr, state, dispatch }) => {
        const { from, to } = state.selection;
        state.doc.nodesBetween(from, to, (node, pos) => { if (node.type.name === "paragraph" || node.type.name === "heading") tr.setNodeMarkup(pos, undefined, { ...node.attrs, [name]: value }); });
        if (dispatch) dispatch(tr); return true;
      },
      setListStyle: (listStyle) => ({ tr, state, dispatch }) => {
        const { $from } = state.selection;
        for (let d = $from.depth; d > 0; d--) { const n = $from.node(d); if (n.type.name === "orderedList") { tr.setNodeMarkup($from.before(d), undefined, { ...n.attrs, listStyle }); if (dispatch) dispatch(tr); return true; } }
        return false;
      },
    };
  },
});

export const MatterImage = Image.extend({
  addAttributes() { return { ...this.parent?.(), documentId: { default: null }, width: { default: null, renderHTML: (a) => a.width ? { style: `width:${a.width}px` } : {} }, align: { default: "center", renderHTML: (a) => ({ "data-align": a.align }) } }; },
});

export const STYLE_PRESETS = {
  Title: { heading: 1, textAlign: "center", spaceAfter: 12 },
  "Cause Title": { heading: null, textAlign: "center", styleName: "cause-title", lineHeight: "1.2" },
  "Court Heading": { heading: 2, textAlign: "center", styleName: "court-heading" },
  "Heading 1": { heading: 1 }, "Heading 2": { heading: 2 }, "Heading 3": { heading: 3 },
  Body: { heading: null, textAlign: "justify", styleName: null, indent: null, firstLineIndent: null },
  "Legal Paragraph": { heading: null, textAlign: "justify", styleName: "legal-paragraph", firstLineIndent: 12.7, lineHeight: "1.5" },
  "Ground Heading": { heading: 3, textAlign: "left", styleName: "ground-heading" },
  Prayer: { heading: null, textAlign: "justify", styleName: "prayer", indent: 1 },
  Quotation: { heading: null, textAlign: "justify", styleName: "quotation", indent: 2, lineHeight: "1.15" },
  "Annexure Heading": { heading: 3, textAlign: "center", styleName: "annexure-heading" },
  Signature: { heading: null, textAlign: "right", styleName: "signature" },
  Verification: { heading: null, textAlign: "justify", styleName: "verification" },
};

export function applyStyle(editor, name) {
  const p = STYLE_PRESETS[name];
  if (!p) return;
  const chain = editor.chain().focus();
  if (p.heading) chain.setHeading({ level: p.heading }); else chain.setParagraph();
  if (p.textAlign) chain.setTextAlign(p.textAlign);
  chain.run();
  ["styleName", "indent", "firstLineIndent", "lineHeight", "spaceAfter"].forEach((k) => { if (k in p) editor.commands.setParagraphAttr(k, p[k]); });
}
